import { ActionType, TenantRegion, UserId } from "@sp-crm/core";
import { QueryRenderer } from "components/clients/show-client/community-comparison/query-renderer";
import { ContentFull, ContentHeader } from "components/layout";
import { fancyConfirm } from "components/ui/fancy-confirm";
import { InlineBanner } from "components/ui/inline-banner";
import { Input } from "components/ui/input";
import { defaultLinkStyle } from "components/ui/link";
import { PrimaryButton } from "components/ui/primary-button";
import { SecondaryButton } from "components/ui/secondary-button";
import { SelectPro } from "components/ui/select-pro";
import { Spinner } from "components/ui/spinner";
import { Formik, FormikHelpers } from "formik";
import {
    EntityRegionPermissionType,
    useAdminUpdateUserMutation,
    UserManagementGetUserQuery,
    UserType,
    useUserManagementDisableUserMutation,
    useUserManagementEnableUserMutation,
    useUserManagementGetRolesQuery,
    useUserManagementGetUserQuery,
    useUserManagementHideUserMutation,
    useUserManagementShowUserMutation,
    useUserManagementUpdatePermissionMutation,
} from "generated/graphql";
import React from "react";
import { useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { useRouteMatch } from "react-router";
import { Link } from "react-router-dom";
import { useProductName } from "store/selectors/branding";
import { useCurrentUserId, useRegionsWithPermission } from "store/selectors/hooks";
import { ApplicationState } from "store/state";
import { stableQueryOptions } from "util/requests";
import { CustomRolesBanner } from "./custom-roles-banner";
import { displayforUser, invalidateUserList, statusForUser } from "./helper";
import { ManageUsersPermissionsLevels } from "./manage-users-permissions";

interface UpdateUserDataFormValues {
    name: string;
}

export const ManageUsersEdit: React.FC<unknown> = () => {
    const { params, path } = useRouteMatch<{ userId: UserId }>();
    const productName = useProductName();
    const user = useUserManagementGetUserQuery({ userId: params.userId });
    const regionsWithModifyAccess = useRegionsWithPermission(
        ActionType.SelfManageModifyUser,
    );
    const disableMutation = useUserManagementDisableUserMutation();
    const enableMutation = useUserManagementEnableUserMutation();
    const showMutation = useUserManagementShowUserMutation();
    const hideMutation = useUserManagementHideUserMutation();
    const queryClient = useQueryClient();
    const refetch = React.useCallback(() => {
        user.refetch();
        invalidateUserList(queryClient);
    }, [user, queryClient]);
    const disableUser = React.useCallback(async () => {
        const proceed = await fancyConfirm(
            "Deactivate account",
            "Are you sure you want to deactivate this account?",
            "Deactivate account",
            "Cancel",
        );
        if (proceed) {
            await disableMutation.mutateAsync({ userId: params.userId });
            refetch();
        }
    }, [disableMutation, params.userId, refetch]);
    const enableUser = React.useCallback(async () => {
        await enableMutation.mutateAsync({ userId: params.userId });
        refetch();
    }, [enableMutation, params.userId, refetch]);
    const hideUser = React.useCallback(async () => {
        await hideMutation.mutateAsync({ userId: params.userId });
        refetch();
    }, [hideMutation, params.userId, refetch]);
    const showUser = React.useCallback(async () => {
        await showMutation.mutateAsync({ userId: params.userId });
        refetch();
    }, [showMutation, params.userId, refetch]);

    const myUserId = useCurrentUserId();

    return (
        <ContentFull>
            <QueryRenderer name="Manage user" query={user}>
                {data => (
                    <>
                        <ContentHeader>
                            Edit account {displayforUser(data.userManagementGetUser.user)}
                        </ContentHeader>
                        <div className="space-y-6">
                            <div>
                                Status: {statusForUser(data.userManagementGetUser.user)}
                            </div>
                            {data.userManagementGetUser.user.id === myUserId ? (
                                <div className="max-w-md">
                                    <InlineBanner type="info">
                                        You are currently viewing your own user account.
                                        Use your Personal Settings to manage your account.
                                    </InlineBanner>
                                </div>
                            ) : null}
                            {regionsWithModifyAccess.map(r => (
                                <ManageUsersEditRegion
                                    refetch={refetch}
                                    key={r.key}
                                    region={r}
                                    user={data.userManagementGetUser.user}
                                    disabled={
                                        data.userManagementGetUser.user.id === myUserId
                                    }
                                />
                            ))}
                            {!data.userManagementGetUser.canDisable &&
                            myUserId !== data.userManagementGetUser.user.id ? (
                                <div className="max-w-md">
                                    <InlineBanner type="info">
                                        This user account is managed in additional{" "}
                                        {productName} regions, so some fields will be
                                        disabled. Contact your administrator for
                                        additional access to make full changes to this
                                        user.
                                    </InlineBanner>
                                </div>
                            ) : null}
                            <UpdateUserForm
                                initialName={data.userManagementGetUser.user.name}
                                userId={params.userId}
                                refetch={refetch}
                                disabled={
                                    !data.userManagementGetUser.canDisable ||
                                    params.userId === myUserId
                                }
                            />
                            <div className="space-x-4">
                                {data.userManagementGetUser.canDisable &&
                                !data.userManagementGetUser.user.disabled ? (
                                    <SecondaryButton onClick={disableUser}>
                                        Deactivate account
                                    </SecondaryButton>
                                ) : null}
                                {data.userManagementGetUser.user.disabled ? (
                                    <>
                                        <SecondaryButton
                                            onClick={enableUser}
                                            disabled={
                                                !data.userManagementGetUser.canDisable
                                            }>
                                            Reactivate account
                                        </SecondaryButton>
                                        {data.userManagementGetUser.user.type ===
                                        UserType.User ? (
                                            data.userManagementGetUser.user.hidden ? (
                                                <SecondaryButton
                                                    disabled={
                                                        !data.userManagementGetUser
                                                            .canDisable
                                                    }
                                                    onClick={showUser}>
                                                    Show user
                                                </SecondaryButton>
                                            ) : (
                                                <SecondaryButton
                                                    disabled={
                                                        !data.userManagementGetUser
                                                            .canDisable
                                                    }
                                                    onClick={hideUser}>
                                                    Hide user
                                                </SecondaryButton>
                                            )
                                        ) : null}
                                    </>
                                ) : null}
                            </div>
                        </div>
                    </>
                )}
            </QueryRenderer>
            <div className="mt-8">
                {path?.includes("integration") ? (
                    <Link
                        className={`${defaultLinkStyle} twoverride`}
                        to="/settings/integrations/accounts">
                        Back to integrations
                    </Link>
                ) : (
                    <Link
                        className={`${defaultLinkStyle} twoverride`}
                        to="/settings/users">
                        Back to accounts
                    </Link>
                )}
            </div>
        </ContentFull>
    );
};

interface ManageUsersEditRegionProps {
    region: TenantRegion;
    user: UserManagementGetUserQuery["userManagementGetUser"]["user"];
    refetch: () => void;
    disabled?: boolean;
}

const ManageUsersEditRegion: React.FC<ManageUsersEditRegionProps> = props => {
    const { region, user, refetch } = props;
    const showRegion = useSelector(
        (state: ApplicationState) => state.region.regions.length > 1,
    );
    const roles = useUserManagementGetRolesQuery({}, stableQueryOptions());
    const upstreamPermission = user.permissions.find(p => p.region.key === region.key);
    const upstreamPermissionValue = upstreamPermission?.role ?? "";
    const upstreamPermissionType = upstreamPermission?.region.membershipType;
    const disabled =
        props.disabled || upstreamPermissionType === EntityRegionPermissionType.Implied;
    const [selectedPermission, setSelectedPermission] = React.useState(
        upstreamPermissionValue.startsWith("custom:")
            ? "custom"
            : upstreamPermissionValue,
    );
    const [showSuccessNotification, setShowSuccessNotification] = React.useState(false);
    const dirty = selectedPermission !== upstreamPermissionValue;
    const mutation = useUserManagementUpdatePermissionMutation();
    const save = React.useCallback(async () => {
        await mutation.mutateAsync({
            userId: user.id,
            regionId: region.id,
            roleKey: selectedPermission,
        });
        setShowSuccessNotification(true);
        setTimeout(() => setShowSuccessNotification(false), 3000);
        refetch();
    }, [selectedPermission, region, refetch, mutation, user.id]);

    React.useEffect(() => {
        if (upstreamPermissionValue.startsWith("custom:")) {
            setSelectedPermission("custom");
        } else {
            setSelectedPermission(upstreamPermissionValue);
        }
    }, [upstreamPermissionValue, user.id, region.id]);

    return (
        <div className="space-y-2">
            <div>
                <QueryRenderer name={`Edit permission for ${region.name}`} query={roles}>
                    {data => (
                        <div className="w-112">
                            <div className="flex items-end space-x-4">
                                <div className="flex-1">
                                    <SelectPro
                                        includePlaceholderOption={false}
                                        label={
                                            showRegion
                                                ? `Permissions for ${region.name}`
                                                : "Permission"
                                        }
                                        name="role"
                                        onChange={e =>
                                            setSelectedPermission(e.target.value)
                                        }
                                        value={selectedPermission}
                                        options={[
                                            { value: "", text: "No access" },
                                            ...data.userManagementGetRoles.map(role => ({
                                                value: role.key,
                                                text:
                                                    upstreamPermissionType ===
                                                    EntityRegionPermissionType.Implied
                                                        ? `${role.humanReadableName} (Inherited)`
                                                        : role.humanReadableName,
                                            })),
                                            { value: "custom", text: "Custom" },
                                        ]}
                                        disabled={disabled}
                                    />
                                </div>
                                <div className="flex-none">
                                    {dirty && selectedPermission !== "custom" ? (
                                        <PrimaryButton onClick={save} disabled={disabled}>
                                            Update
                                        </PrimaryButton>
                                    ) : (
                                        <SecondaryButton disabled>Update</SecondaryButton>
                                    )}
                                </div>
                            </div>
                            <ManageUsersPermissionsLevels />
                            {selectedPermission === "custom" ? (
                                <CustomRolesBanner />
                            ) : null}
                            {showSuccessNotification ? (
                                <div className="mt-4">
                                    <InlineBanner type="success">
                                        Permission updated
                                    </InlineBanner>
                                </div>
                            ) : null}
                        </div>
                    )}
                </QueryRenderer>
            </div>
        </div>
    );
};

interface UpdateUserFormProps {
    initialName: string;
    userId: UserId;
    disabled?: boolean;
    refetch: () => void;
}

const UpdateUserForm: React.FC<UpdateUserFormProps> = ({
    initialName,
    userId,
    refetch,
    disabled,
}) => {
    const updateMutation = useAdminUpdateUserMutation();
    const [showSuccessNotification, setShowSuccessNotification] = React.useState(false);

    const handleSubmit = React.useCallback(
        (
            values: UpdateUserDataFormValues,
            helpers: FormikHelpers<UpdateUserDataFormValues>,
        ) => {
            const payload = {
                userId: userId,
                name: values.name,
            };
            (async () => {
                await updateMutation.mutateAsync(payload);
                setShowSuccessNotification(true);
                setTimeout(() => setShowSuccessNotification(false), 3000);
                refetch();
                helpers.setSubmitting(false);
            })();
        },
        [updateMutation, userId, refetch],
    );

    return (
        <>
            <Formik
                onSubmit={handleSubmit}
                initialValues={{
                    name: initialName,
                }}>
                {({ values, handleChange, handleSubmit, isSubmitting }) => (
                    <form className="space-y-4 max-w-md" onSubmit={handleSubmit}>
                        <Input
                            label="Name"
                            name="name"
                            value={values.name}
                            onChange={handleChange}
                            disabled={disabled}
                        />
                        <PrimaryButton
                            type="submit"
                            disabled={
                                values.name === initialName || isSubmitting || disabled
                            }>
                            {isSubmitting ? <Spinner /> : "Save"}
                        </PrimaryButton>
                    </form>
                )}
            </Formik>
            {showSuccessNotification && (
                <div className="mt-4">
                    <InlineBanner type="success">User updated successfully</InlineBanner>
                </div>
            )}
        </>
    );
};
